import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useGetCockpitDataMutation } from "src/context/api/cockpit/api";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { Autocomplete, Chip, IconButton, Menu, MenuItem, Select, TextField, useTheme } from '@mui/material';
import Typography from "@mui/material/Typography";
import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton,
    GridPagination,
    GridColumnVisibilityModel,
} from "@mui/x-data-grid-pro";
import Skeleton from "@mui/material/Skeleton";
import { formatCurrency } from "src/utils/format-currency";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import CompareChip from "src/components/chip/compare-chip";
import CustomLineChart from "src/components/inline-chart/inline-chart";
import { formatNumber, fPercent } from "src/utils/format-number";
import Iconify from "src/components/iconify";
import Tooltip from "@mui/material/Tooltip";
import { useRouter } from "src/routes/hooks/use-router";
import { useBoolean } from "src/hooks/use-boolean";
import { useCurrentUserQuery } from "src/context/api/auth";
import { useGetWorkspacesQuery } from "src/context/api/workspaces";
import { setFilterCompare, setFilterDate, setFilterDateCompare } from "src/context/reducers/filter-settings/filterSlice";
import { useDispatch } from "react-redux";
import { subDays } from "date-fns";

type CockpitProps = {
    viewMode?: 'list' | 'grid' | 'unset';
    setViewMode?: any;
    demoMode?: boolean;
};

export default function Cockpit({ viewMode, setViewMode, demoMode }: CockpitProps) {
    const [getCockpitData, { data, isLoading, error }] = useGetCockpitDataMutation();
    const [treeRows, setTreeRows] = useState<any[]>([]);
    const workspace = useCurrentWorkspace();
    const lang = localStorage.getItem("i18nextLng") || "en";
    const theme = useTheme();
    const router = useRouter();
    const user: any = useCurrentUserQuery();
    const workspaces = useGetWorkspacesQuery(null);
    const role = user?.data?.role;
    const newWorkspaceModal = useBoolean(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (["admin", "brand specialist"].includes(role) && location?.pathname?.split("/")[2] === "add") {
            newWorkspaceModal.onTrue()
        }
    }, [location.pathname])

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });

    const dateRangeOptions = [
        { label: 'Last 7 days', value: 'last_7' },
        { label: 'Last 14 days', value: 'last_14' },
        { label: 'Last 30 days', value: 'last_30' },
    ];

    const comparisonPeriodOptions = [
        { label: 'Prev. Period', value: 'prev_period' },
        { label: 'Last Year', value: 'last_year' },
    ];

    const tabs = [
        {
            label: 'All',
            countBadge: "rgba(145, 158, 171, 0.2)",
            color: "rgba(51, 60, 71, 1)",
            countBadgeActive: "#1c252e",
            colorActive: "white"
        },
        {
            label: 'Blended CTR dropped of 3pp',
            countBadge: "rgba(255, 228, 222, 1)",
            color: "#b71d18",
            countBadgeActive: "#ff5630",
            colorActive: "white"
        },
        {
            label: 'Blended CPC increased of 5%',
            countBadge: "#fff1d6",
            color: "#b76e00",
            countBadgeActive: "#ffab00",
            colorActive: "#1c252e"
        },
        {
            label: 'Brand Searches increased of 10%',
            countBadge: "rgba(52, 152, 219, 0.2)",
            color: "#0052B4",
            countBadgeActive: "#1c252e",
            colorActive: "#ffffff",
        }
    ];

    const [selectedTab, setSelectedTab] = useState('All');
    const [selectedDateRange, setSelectedDateRange] = useState(dateRangeOptions[0]);
    const [selectedComparisonPeriod, setSelectedComparisonPeriod]: any = useState(comparisonPeriodOptions[0]);

    const TooltipInfoValue = ({ labelText, valueText }: any) => {
        const textColor = theme.palette.mode === 'light' ? '#212B36' : '#fff';
        const tooltipBgColor = theme.palette.mode === 'light' ? '#fff' : 'rgb(31, 38, 49)';
        const innerBgColor = theme.palette.mode === 'light' ? '#F4F6F8' : '#191f28';

        return (
            <div
                style={{
                    zIndex: 9900,
                    borderRadius: 8,
                    color: textColor,
                    lineHeight: 3.2,
                    textAlign: "center",
                    backgroundColor: tooltipBgColor,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                }}
            >
                <div style={{
                    textAlign: "center",
                    lineHeight: 2,
                    marginBottom: 1,
                }}>
                    <span style={{
                        fontWeight: 600,
                        fontSize: 12,
                        fontFamily: "Public Sans"
                    }}>{selectedComparisonPeriod?.label}</span>
                </div>
                <div style={{
                    backgroundColor: innerBgColor,
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 1,
                    padding: "2px 8px",
                    borderRadius: 8
                }}>
                    <span style={{ fontSize: 12 }}>{labelText}</span>
                    <span style={{ fontSize: 12, fontWeight: "bold", marginLeft: 4 }}>{valueText}</span>
                </div>
            </div>
        );
    }

    // Création des lignes en treeData
    useEffect(() => {
        if (!data) return;
        let rows: any[] = [];

        data.forEach((e: any) => {
            // --- Transformer le tableau 'regions' en maps et sparklines par pays ---
            const adCostByCountry: Record<string, number> = {};
            const adCostByCountryCompare: Record<string, number> = {};
            const blendedCtrByCountry: Record<string, number> = {};
            const blendedCtrByCountryCompare: Record<string, number> = {};
            const sparklineByCountry: Record<string, { date: string; value: number }[]> = {};
            const sparklineBlendedCtrByCountry: Record<string, { date: string; value: number }[]> = {};
            const sparklineBlendedCpcByCountry: Record<string, { date: string; value: number }[]> = {};
            const activationsByCountry: Record<string, string[]> = {};
            const brandSearchesByCountry: Record<string, number> = {};
            const brandSearchesByCountryCompare: Record<string, number> = {};
            const sparklineBrandSearchesByCountry: Record<string, { date: string; value: number }[]> = {};
            const sparklineBrandConversionsByCountry: Record<string, { date: string; value: number }[]> = {};
            const sparklineAdCostActivatedByCountry: Record<string, { date: string; value: number }[]> = {};
            const adCostActivatedByCountry: Record<string, number> = {};
            const adCostActivatedByCountryCompare: Record<string, number> = {};
            const blendedCtrActivatedByCountry: Record<string, number> = {};
            const blendedCtrActivatedByCountryCompare: Record<string, number> = {};
            const sparklineBlendedCtrActivatedByCountry: Record<string, { date: string, value: number }[]> = {};
            const blendedCpcActivatedByCountry: Record<string, number> = {};
            const blendedCpcActivatedByCountryCompare: Record<string, number> = {};
            const sparklineBlendedCpcActivatedByCountry: Record<string, { date: string; value: number }[]> = {};
            const blendedCpcByCountry: Record<string, number> = {};
            const blendedCpcByCountryCompare: Record<string, number> = {};

            (e.regions || []).forEach((r: any) => {
                const c = r.region_name;
                adCostByCountry[c] = r.ad_cost_by_region;
                adCostByCountryCompare[c] = r.ad_cost_by_region_compare || 0;
                blendedCpcByCountry[c] = r.blended_cpc_by_region || 0;
                blendedCpcByCountryCompare[c] = r.blended_cpc_by_region_compare || 0;
                blendedCtrByCountry[c] = r.blended_ctr_by_region;
                blendedCtrByCountryCompare[c] = r.blended_ctr_by_region_compare || 0; 0
                sparklineByCountry[c] = (r.sparkline_ad_cost_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ad_cost_by_region }));
                sparklineBlendedCtrByCountry[c] = (r.sparkline_ctr_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ctr_by_region }));
                sparklineBlendedCpcByCountry[c] = (r.sparkline_cpc_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_cpc_by_region }));
                activationsByCountry[c] = r.activations_by_region || [];
                brandSearchesByCountry[c] = r.brand_searches_by_region;
                brandSearchesByCountryCompare[c] = r.brand_searches_by_region_compare || 0;
                sparklineBrandSearchesByCountry[c] = (r.sparkline_brand_searches_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_brand_searches_by_region }));
                sparklineBrandConversionsByCountry[c] = (r.sparkline_conversions_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_conversions_by_region }));
                sparklineAdCostActivatedByCountry[c] = (r.sparkline_ad_cost_activated_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ad_cost_activated_by_region }));
                adCostActivatedByCountry[c] = r.ad_cost_activated_by_region || 0;
                adCostActivatedByCountryCompare[c] = r.ad_cost_activated_by_region_compare || 0;
                blendedCtrActivatedByCountry[c] = r.blended_ctr_activated_by_region || 0;
                blendedCtrActivatedByCountryCompare[c] = r.blended_ctr_activated_by_region_compare || 0;
                sparklineBlendedCtrActivatedByCountry[c] = (r.sparkline_blended_ctr_activated_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.blended_ctr_activated }));
                blendedCpcActivatedByCountry[c] = r.blended_cpc_activated_by_region || 0;
                blendedCpcActivatedByCountryCompare[c] = r.blended_cpc_activated_by_region_compare || 0;
                sparklineBlendedCpcActivatedByCountry[c] = (r.sparkline_blended_cpc_activated_by_region || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_blended_cpc_activated_by_region }));
            });


            // Union global des activations
            const globalActivations = Array.from(new Set(Object.values(activationsByCountry).flat()));

            // Remapper les sparklines globales
            const sparkline = (e.sparkline_ad_cost || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ad_cost }));
            const sparklineBlendedCtr = (e.sparkline_ctr || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ctr }));
            const sparklineBlendedCpc = (e.sparkline_cpc || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_cpc }));
            const sparklineBrandSearches = (e.sparkline_brand_searches || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_brand_searches }));
            const sparklineBrandConversions = (e.sparkline_conversions || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_conversions }));
            const sparklineAdCostActivated = (e.sparkline_ad_cost_activated || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ad_cost_activated }));
            const sparklineCtrActivated = (e.sparkline_ctr_activated || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_ctr_activated }));
            const sparklineCpcActivated = (e.sparkline_cpc_activated || []).map((pt: any) => ({ date: pt.date_day, value: pt.daily_cpc_activated }));

            // Pusher les lignes parent/enfant selon nombre de pays
            const countries = Object.entries(adCostByCountry);
            if (countries.length === 1) {
                const [country, cost] = countries[0];
                rows.push({
                    id: e.client,
                    client: e.client,
                    isParent: true,
                    ad_cost: e.ad_cost_total,
                    ad_cost_compare: e.ad_cost_total_compare,
                    blended_ctr: e.blended_ctr_total,
                    blended_ctr_compare: e.blended_ctr_total_compare,
                    sparkline_blended_ctr: sparklineBlendedCtr,
                    sparkline: sparkline,
                    activations: globalActivations,
                    brand_searches_total: e.brand_searches_total,
                    brand_searches_total_compare: e.brand_searches_total_compare,
                    sparkline_brand_searches: sparklineBrandSearches,
                    blended_cpc_total: e.blended_cpc_total,
                    blended_cpc_total_compare: e.blended_cpc_total_compare,
                    sparkline_blended_cpc: sparklineBlendedCpc,
                    blended_conversions_total: e.blended_conversions_total,
                    blended_conversions_total_compare: e.blended_conversions_total_compare,
                    sparkline_brand_conversions: sparklineBrandConversions,
                    workspace_name: e.workspace_name,
                    workspace_template: e.workspace_template,
                    workspace_id: e.workspace_id,
                    workspace_currency: e.workspace_currency,
                    ad_cost_activated: e.ad_cost_activated,
                    ad_cost_activated_compare: e.ad_cost_activated_compare,
                    blended_ctr_activated: e.blended_ctr_activated,
                    blended_ctr_activated_compare: e.blended_ctr_activated_compare,
                    blended_cpc_activated: e.blended_cpc_activated,
                    blended_cpc_activated_compare: e.blended_cpc_activated_compare,
                    sparkline_ad_cost_activated: sparklineAdCostActivated,
                    sparkline_ctr_activated: sparklineCtrActivated,
                    sparkline_cpc_activated: sparklineCpcActivated
                });
            } else {
                // ligne parent
                rows.push({
                    id: e.client,
                    client: e.client,
                    isParent: true,
                    ad_cost: e.ad_cost_total,
                    ad_cost_compare: e.ad_cost_total_compare,
                    blended_ctr: e.blended_ctr_total,
                    blended_ctr_compare: e.blended_ctr_total_compare,
                    sparkline_blended_ctr: sparklineBlendedCtr,
                    sparkline: sparkline,
                    activations: globalActivations,
                    brand_searches_total: e.brand_searches_total,
                    brand_searches_total_compare: e.brand_searches_total_compare,
                    sparkline_brand_searches: sparklineBrandSearches,
                    blended_cpc_total: e.blended_cpc_total,
                    blended_cpc_total_compare: e.blended_cpc_total_compare,
                    sparkline_blended_cpc: sparklineBlendedCpc,
                    blended_conversions_total: e.blended_conversions_total,
                    blended_conversions_total_compare: e.blended_conversions_total_compare,
                    sparkline_brand_conversions: sparklineBrandConversions,
                    workspace_name: e.workspace_name,
                    workspace_template: e.workspace_template,
                    workspace_id: e.workspace_id,
                    workspace_currency: e.workspace_currency,
                    ad_cost_activated: e.ad_cost_activated,
                    ad_cost_activated_compare: e.ad_cost_activated_compare,
                    blended_ctr_activated: e.blended_ctr_activated,
                    blended_ctr_activated_compare: e.blended_ctr_activated_compare,
                    blended_cpc_activated: e.blended_cpc_activated,
                    blended_cpc_activated_compare: e.blended_cpc_activated_compare,
                    sparkline_ad_cost_activated: sparklineAdCostActivated,
                    sparkline_ctr_activated: sparklineCtrActivated,
                    sparkline_cpc_activated: sparklineCpcActivated
                });

                // lignes enfants par pays
                countries.forEach(([c, cost]) => {
                    rows.push({
                        id: `${e.client}__${c}`,
                        client: e.client,
                        country: c,
                        parentId: e.client,
                        isParent: false,
                        ad_cost: cost,
                        ad_cost_compare: adCostByCountryCompare[c] || 0,
                        blended_ctr: blendedCtrByCountry[c] || 0,
                        blended_ctr_compare: blendedCtrByCountryCompare[c] || 0,
                        sparkline: sparklineByCountry[c] || [],
                        sparkline_blended_ctr: sparklineBlendedCtrByCountry[c] || [],
                        sparkline_blended_cpc: sparklineBlendedCpcByCountry[c] || [],
                        activations: activationsByCountry[c] || [],
                        brand_searches_total: brandSearchesByCountry[c] || 0,
                        brand_searches_total_compare: brandSearchesByCountryCompare[c] || 0,
                        sparkline_brand_searches: sparklineBrandSearchesByCountry[c] || [],

                        blended_cpc_total: blendedCpcByCountry[c] || 0,
                        blended_cpc_total_compare: blendedCpcByCountryCompare[c] || 0,

                        sparkline_brand_conversions: sparklineBrandConversionsByCountry[c] || [],
                        workspace_name: e.workspace_name,
                        workspace_template: e.workspace_template,
                        workspace_currency: e.workspace_currency,
                        ad_cost_activated: adCostActivatedByCountry[c] || 0,
                        ad_cost_activated_compare: adCostActivatedByCountryCompare[c] || 0,
                        sparkline_ad_cost_activated: sparklineAdCostActivatedByCountry[c] || [],
                        blended_ctr_activated: blendedCtrActivatedByCountry[c] || 0,
                        blended_ctr_activated_compare: blendedCtrActivatedByCountryCompare[c] || 0,
                        sparkline_ctr_activated: sparklineBlendedCtrActivatedByCountry[c] || [],
                        blended_cpc_activated: blendedCpcActivatedByCountry[c] || 0,
                        blended_cpc_activated_compare: blendedCpcActivatedByCountryCompare[c] || 0,
                        sparkline_cpc_activated: sparklineBlendedCpcActivatedByCountry[c] || [],
                    });
                });
            }
        });

        setTreeRows(rows);
    }, [data]);

    // ACTIONS
    const ActionMenu = ({ params }: { params: any }) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
        };

        const handleClose = (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setAnchorEl(null);
        };

        const handleRedirectTrafficDetails = () => {
            router.push(`/brand/search-performance/explore?workspace=${params?.row?.workspace_id}`);
        }


        const handleRedirectActivate = () => {
            router.push(`/brand/brand-shield/ko-bidding/max-cpc?workspace=${params?.row?.workspace_id}`);
        }

        return (
            <>
                <IconButton onClick={handleClick} sx={{ cursor: 'pointer' }} >
                    <Iconify color={"rgba(51, 60, 71, 1)"} width={13} height={13} icon="fa-solid:ellipsis-v" />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    onClick={(e) => e.stopPropagation()}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <MenuItem onClick={handleRedirectTrafficDetails}>
                        <Iconify icon={"fluent:arrow-trending-lines-20-filled"} />

                        <Box sx={{ position: 'relative', top: 2 }}>
                            <Typography sx={{ fontSize: 14, ml: .5 }}>Analyse</Typography>
                        </Box>
                    </MenuItem>
                    <MenuItem onClick={handleRedirectActivate}>
                        <Iconify icon={"mdi:gear"} />

                        <Box sx={{ position: 'relative', top: 2 }}>
                            <Typography sx={{ fontSize: 14, ml: .5 }}>Activate</Typography>
                        </Box>
                    </MenuItem>
                </Menu>
            </>
        );
    }

    // TOUTES TES COLONNES, sans rien perdre
    const columns: GridColDef[] = [
        {
            field: 'workspace_name',
            headerName: 'Workspace',
            width: 250,
            disableColumnMenu: true,
            filterable: true,
            sortable: true,
        },
        {
            field: "ad_cost",
            headerName: "Ad Cost",
            flex: 1,
            minWidth: 180,
            type: "number",
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = params.row.ad_cost as number;
                const prev = params.row.ad_cost_compare as number;
                const pct = prev ? ((cur - prev) / prev) * 100 : NaN;

                const sparkSeries = [
                    {
                        name: 'Ad Cost',
                        data: ((params.row.sparkline || []) as { date: string; value: number }[]).map(
                            (pt) => ({ x: new Date(pt.date).getTime(), y: pt.value })
                        ),
                    },
                ];

                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', overflow: 'visible', justifyContent: "flex-end" }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12, color: "rgba(51, 60, 71, 1)" }}>
                            {formatCurrency(cur, params.row.workspace_currency || workspace?.currency, lang, true, 2)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Ad Cost:"
                                    valueText={formatCurrency(prev, params.row.workspace_currency || workspace?.currency, lang, true, 2)} z
                                />
                            }
                            placement="top"
                            arrow
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip invertedColor={true} value={pct} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: "blended_ctr",
            flex: 1,
            headerName: "Blended CTR",
            minWidth: 180,
            type: "number",
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = params.row.blended_ctr as number;
                const prev = params.row.blended_ctr_compare as number;
                const delta_pp = cur - prev;

                const sparkSeries = [
                    {
                        name: 'Blended CTR',
                        data: ((params.row.sparkline_blended_ctr || []) as { date: string; value: number }[]).map(
                            (pt) => ({ x: new Date(pt.date).getTime(), y: pt.value })
                        ),
                    },
                ];

                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'flex-end', color: "rgba(51, 60, 71, 1)" }} >
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            {fPercent(cur)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Blended CTR:"
                                    valueText={fPercent(prev)}
                                />
                            }
                            placement="top"
                            arrow
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip value={delta_pp} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: "blended_cpc_total",
            headerName: "Blended CPC",
            flex: 1,
            type: "number",
            width: 265,
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = params.row.blended_cpc_total as number;
                const prev = params.row.blended_cpc_total_compare as number;
                const pct = prev ? ((cur - prev) / prev) * 100 : NaN;
                const sparkSeries = [
                    {
                        name: 'Blended CPC',
                        data: (params.row.sparkline_blended_cpc || []).map((pt: any) => ({
                            x: new Date(pt.date).getTime(),
                            y: pt.value,
                        })),
                    },
                ];
                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'flex-end', overflow: 'visible', color: "rgba(51, 60, 71, 1)" }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            {formatCurrency(cur, params.row.workspace_currency || workspace?.currency, lang, false, 2)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Blended CPC"
                                    valueText={formatCurrency(prev, params.row.workspace_currency || workspace?.currency, lang, false, 2)}
                                />
                            }
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            placement="top"
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip invertedColor={true} value={pct} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: "brand_searches_total",
            type: "number",
            flex: 1,
            headerName: "Brand Searches",
            width: 225,
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = params.row.brand_searches_total as number;
                const prev = params.row.brand_searches_total_compare as number;
                const pct = prev ? ((cur - prev) / prev) * 100 : NaN;
                const sparkSeries = [
                    {
                        name: 'Brand Searches',
                        data: (params.row.sparkline_brand_searches || []).map((pt: any) => ({
                            x: new Date(pt.date).getTime(),
                            y: pt.value,
                        })),
                    },
                ];
                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'flex-end', overflow: 'visible', color: "rgba(51, 60, 71, 1)" }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            {formatNumber(cur)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Brand Searches"
                                    valueText={formatNumber(prev)}
                                />
                            }
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            placement="top"
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip value={pct} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: "blended_conversions_total",
            type: "number",
            headerName: "Brand Conversions / Revenue",
            width: 300,
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = params.row.blended_conversions_total as number;
                const prev = params.row.blended_conversions_total_compare as number;
                const pct = prev ? ((cur - prev) / prev) * 100 : NaN;

                const sparkSeries = [
                    {
                        name: 'Brand Conversions',
                        data: (params.row.sparkline_brand_conversions || []).map((pt: any) => ({
                            x: new Date(pt.date).getTime(),
                            y: pt.value,
                        })),
                    },
                ];

                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible',
                                }}
                            >
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="line"
                                    color="#006c9c"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) =>
                                        params.row.workspace_template === 'E-commerce'
                                            ? formatCurrency(value, params.row.workspace_currency || workspace?.currency, lang, false, 0)
                                            : formatNumber(value)
                                    }
                                />
                            </Box>
                            {params.row.workspace_template === 'E-commerce'
                                ? formatCurrency(cur, params.row.workspace_currency || workspace?.currency, lang, false, 0)
                                : formatNumber(cur)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Brand Conversions"
                                    valueText={
                                        params.row.workspace_template === 'E-commerce'
                                            ? formatCurrency(prev, params.row.workspace_currency || workspace?.currency, lang, false, 0)
                                            : formatNumber(prev)
                                    } />
                            }
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            placement="top"
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                {pct ? <CompareChip value={pct} /> : '-'}
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            }
        },
        {
            field: "ad_cost_activated",
            type: "number",
            headerName: "Ad Cost Activated",
            width: 300,
            renderCell: (params: GridRenderCellParams<any>) => {
                const rawCur = params.row.ad_cost_activated as number;
                const cur = Number.isFinite(rawCur) ? rawCur : 0;
                const rawPrev = params.row.ad_cost_activated_compare as number;
                const prev = Number.isFinite(rawPrev) ? rawPrev : 0;
                const pct = prev > 0 ? ((cur - prev) / prev) * 100 : 0;

                const isPassive = (params.row.activations?.length || 0) === 0;
                if (isPassive) return "-";

                // Sparkline série
                const sparkSeries = [
                    {
                        name: 'Ad Cost Activated',
                        data: (params.row.sparkline_ad_cost_activated || []).map(
                            (pt: any) => ({ x: new Date(pt.date).getTime(), y: pt.value })
                        ),
                    },
                ];

                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible'
                                }}>
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="bar"
                                    color="#FFD066"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) =>
                                        formatCurrency(value, params.row.workspace_currency || workspace?.currency, lang, true, 2)
                                    }
                                />
                            </Box>
                            {formatCurrency(
                                cur,
                                params.row.workspace_currency || workspace?.currency,
                                lang,
                                true,
                                2
                            )}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Ad Cost Activated:"
                                    valueText={formatCurrency(prev, params.row.workspace_currency || workspace?.currency, lang, true, 2)} z
                                />
                            }
                            placement="top"
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip invertedColor={true} value={pct} />
                            </Box>
                        </Tooltip>

                    </Stack>
                );
            },
        },
        {
            field: "blended_ctr_activated",
            type: "number",
            headerName: "Blended CTR Activated",
            width: 300,
            renderCell: (params: GridRenderCellParams<any>) => {
                const cur = (params.row.blended_ctr_activated as number) * 100;
                const prev = (params.row.blended_ctr_activated_compare as number) * 100;
                const pct = prev ? (cur - prev) / prev : NaN;

                const isPassive = (params.row.activations?.length || 0) === 0;
                if (isPassive) return "-";

                const sparkSeries = [
                    {
                        name: 'Blended CTR Activated',
                        data: (params.row.sparkline_ctr_activated || []).map(
                            (pt: any) => ({ x: new Date(pt.date).getTime(), y: pt.value * 100 })
                        ),
                    },
                ];

                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible'
                                }}>
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="line"
                                    color="#7635dc"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) => fPercent(value)}
                                />
                            </Box>
                            {fPercent(cur)}
                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Blended CTR Activated:"
                                    valueText={fPercent(prev)}
                                />
                            }
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            placement="top"
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip value={pct} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            },
        },
        {
            field: "blended_cpc_activated",
            type: "number",
            headerName: "Blended CPC Activated",
            width: 300,
            renderCell: (params: GridRenderCellParams<any>) => {
                const rawCur = params.row.blended_cpc_activated as number;
                const cur = Number.isFinite(rawCur) ? rawCur : 0;
                const rawPrev = params.row.blended_cpc_activated_compare as number;
                const prev = Number.isFinite(rawPrev) ? rawPrev : 0;
                const pct = prev > 0 ? ((cur - prev) / prev) * 100 : 0;

                const isPassive = (params.row.activations?.length || 0) === 0;
                if (isPassive) return "-";

                const sparkSeries = [
                    {
                        name: 'Blended CPC Activated',
                        data: (params.row.sparkline_cpc_activated || []).map(
                            (pt: any) => ({ x: new Date(pt.date).getTime(), y: pt.value })
                        ),
                    },
                ];

                return (
                    <Stack direction="row" alignItems="center" sx={{ flexDirection: 'row', justifyContent: 'space-between', overflow: 'visible' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', fontSize: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    top: -6,
                                    mr: 1,
                                    overflow: 'visible'
                                }}>
                                <CustomLineChart
                                    series={sparkSeries}
                                    type="line"
                                    color="#FF5630"
                                    height={30}
                                    width={100}
                                    formatValue={(value: any) => value?.toFixed(3)}
                                />
                            </Box>

                            {formatCurrency(
                                cur,
                                params.row.workspace_currency || workspace?.currency,
                                lang,
                                true,
                                2
                            )}

                        </Box>

                        <Tooltip
                            title={
                                <TooltipInfoValue
                                    labelText="Blended CPC Activated"
                                    valueText={formatCurrency(prev, params.row.workspace_currency || workspace?.currency, lang, false, 2)}
                                />
                            }
                            slotProps={{
                                popper: {
                                    sx: {
                                        [`& .MuiTooltip-tooltip`]: {
                                            backgroundColor: 'transparent',
                                        },
                                        [`& .MuiTooltip-arrow`]: {
                                            color: 'rgb(31, 38, 49)', // même couleur pour l'arrow !
                                        },
                                    }
                                }
                            }}
                            placement="top"
                            arrow
                            PopperProps={{ disablePortal: true }}
                        >
                            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                                <CompareChip invertedColor={true} value={pct} />
                            </Box>
                        </Tooltip>
                    </Stack>
                );
            },
        },
        {
            field: "activations",
            headerName: "Mode",
            flex: 1,
            width: 225,
            renderCell: (params: GridRenderCellParams<any>) => {
                const activations = params.row.activations || [];

                if (activations.length === 0) {
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                flexWrap: 'wrap',
                                gap: 1,
                                height: '100%',
                            }}
                        >
                            --
                        </Box>
                    );
                }

                const mapMode: Record<string, { textColor: string; icon: any, bgIcon: string, bg: string }> = {
                    'KO Bidding': {
                        textColor: "rgba(122, 37, 228, 1)",
                        icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.41826 18.0124C9.60275 18.12 9.695 18.1738 9.82519 18.2018C9.92622 18.2234 10.0736 18.2234 10.1747 18.2018C10.3048 18.1738 10.3971 18.12 10.5816 18.0124C12.205 17.0653 16.6666 14.0904 16.6666 9.99999V6.01466C16.6666 5.3484 16.6666 5.01527 16.5576 4.72891C16.4614 4.47594 16.3049 4.25023 16.1019 4.07127C15.872 3.86869 15.5601 3.75172 14.9362 3.51778L10.4681 1.84222C10.2948 1.77725 10.2082 1.74477 10.1191 1.73189C10.0401 1.72047 9.95978 1.72047 9.88074 1.73189C9.79162 1.74477 9.705 1.77725 9.53175 1.84222L5.06359 3.51778C4.43975 3.75172 4.12783 3.86869 3.89797 4.07127C3.6949 4.25023 3.53848 4.47594 3.44222 4.72891C3.33325 5.01527 3.33325 5.3484 3.33325 6.01466V9.99999C3.33325 14.0904 7.79489 17.0653 9.41826 18.0124Z" stroke="#7A25E4" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>,
                        bgIcon: "rgba(164, 111, 247, 0.20)",
                        bg: "rgba(164, 111, 247, 0.12)"
                    },
                    'Active Mode': {
                        textColor: 'rgba(103, 83, 21, 1)',
                        icon: <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                            <path d="M14.0648 15.0653C12.6411 16.0997 10.9264 16.6569 9.16658 16.6569C7.40675 16.6569 5.69209 16.0997 4.26836 15.0653M12.8197 0.833577C14.4014 1.60503 15.6983 2.85743 16.5245 4.41126C17.3507 5.9651 17.6637 7.7406 17.4188 9.4833M0.914422 9.48322C0.669501 7.74052 0.98257 5.96502 1.80876 4.41118C2.63495 2.85735 3.93184 1.60495 5.51356 0.833496M13.7499 8.32353C13.7499 10.8548 11.6979 12.9069 9.16658 12.9069C6.63527 12.9069 4.58324 10.8548 4.58324 8.32353C4.58324 5.79222 6.63527 3.7402 9.16658 3.7402C11.6979 3.7402 13.7499 5.79222 13.7499 8.32353Z" stroke="#AF8604" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>,
                        bgIcon: "rgba(250, 176, 0, 0.1)",
                        bg: "rgba(250, 176, 0, 0.08)"
                    },
                };

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 1,
                            flexWrap: 'nowrap',
                            height: '100%',
                        }}
                    >
                        {activations.map((label: string, idx: number) => {
                            const { textColor, icon, bgIcon, bg } = mapMode[label]

                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        color: textColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: 1,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Box sx={{ bgcolor: bgIcon, width: '100%', p: .2, borderTopLeftRadius: 8, borderBottomLeftRadius: 6 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', px: .25 }}>{icon}</Box>
                                    </Box>
                                    <Box sx={{
                                        backgroundColor: bg,
                                        p: .23,
                                        borderTopRightRadius: 8, borderBottomRightRadius: 6
                                    }}>
                                        <Typography px={.5} fontSize={13}>{label}</Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                );
            },
        },
        {
            field: 'Actions',
            headerName: '',
            width: 50,
            sortable: false,
            filterable: false,
            disableExport: true,
            renderCell: (params: GridRenderCellParams) =>
                params.row.isParent ? <ActionMenu params={params} /> : null,
        }

    ];

    const getTreeDataPath = React.useCallback(
        (row: any) => {
            const ws = (row.workspace_name || row.client || `unknown-${row.id}`)?.trim();
            const country = row.country?.trim();

            return row.parentId && country ? [ws, country] : [ws];
        },
        []
    );

    const apiRef: any = useRef();

    const CustomToolbar = (props: any) => {
        return (
            <Stack
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                direction="row"
                alignItems="center"
                spacing={0}
                px={2.5}
                py={1.25}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: 'white',
                    borderTopRightRadius: "8px",
                    borderTopLeftRadius: "8px",
                    borderBottom: "1px dashed rgba(145, 158, 171, 0.20)",
                }}
            >

                <Stack direction="row" alignItems="center" px={1} spacing={0.5}>
                    <IconButton
                        onClick={() => apiRef.current?.showPreferences("columns")}
                        sx={{
                            width: 26,
                            height: 26,
                            padding: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
                    </IconButton>
                    <IconButton
                        onClick={() => apiRef.current?.showFilterPanel()}
                        sx={{
                            width: 26,
                            height: 26,
                            padding: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="ic:round-filter-list" width={18} height={18} />
                    </IconButton>
                    <IconButton
                        onClick={() => apiRef.current?.exportDataAsCsv({
                            fileName: 'cockpit',
                            utf8WithBom: true
                        })}
                        sx={{
                            width: 24,
                            height: 24,
                            padding: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="lets-icons:export" width={16} height={16} />
                    </IconButton>
                </Stack>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderRadius: 1,
                        px: 2
                    }}
                >
                    <Stack direction="row" spacing={2}>
                        <Autocomplete
                            disablePortal
                            options={dateRangeOptions}
                            size="small"
                            disableClearable
                            onChange={(event, newValue) => setSelectedDateRange(newValue!)}
                            getOptionLabel={(option) => option.label}
                            sx={{ width: 150 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                {selectedDateRange && (
                                                    <Chip
                                                        label={selectedDateRange.label}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: 'rgba(145, 158, 171, 0.32)',
                                                            color: 'rgba(51, 60, 71, 1)',
                                                            mr: 1,
                                                        }}
                                                    />
                                                )}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Autocomplete
                            disablePortal
                            options={comparisonPeriodOptions}
                            disableClearable
                            size="small"
                            onChange={(event, newValue) => setSelectedComparisonPeriod(newValue!)}
                            getOptionLabel={(option) => option.label}
                            sx={{ width: 150 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                {selectedComparisonPeriod && (
                                                    <Chip
                                                        label={selectedComparisonPeriod.label}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: 'rgba(145, 158, 171, 0.32)',
                                                            color: 'rgba(51, 60, 71, 1)',
                                                            mr: 1,
                                                        }}
                                                    />
                                                )}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Stack>
                </Box>
            </Stack>
        )
    }

    const getTabCount = (label: any) => {
        if (label === 'All') {
            return treeRows.filter(r => r.isParent).length;
        }

        let rule: ((row: any) => boolean) | null = null;

        if (label === 'Blended CTR dropped of 3pp') {
            rule = (row) =>
                typeof row.blended_ctr === 'number' &&
                typeof row.blended_ctr_compare === 'number' &&
                row.blended_ctr_compare &&
                (row.blended_ctr * 100 - row.blended_ctr_compare * 100) <= -3;
        }

        if (label === 'Blended CPC increased of 5%') {
            rule = (row) =>
                typeof row.blended_cpc_total === 'number' &&
                typeof row.blended_cpc_total_compare === 'number' &&
                row.blended_cpc_total_compare &&
                (row.blended_cpc_total - row.blended_cpc_total_compare) / row.blended_cpc_total_compare >= 0.05;
        }

        if (label === 'Brand Searches increased of 10%') {
            rule = (row) =>
                typeof row.brand_searches_total === 'number' &&
                typeof row.brand_searches_total_compare === 'number' &&
                row.brand_searches_total_compare &&
                (row.brand_searches_total - row.brand_searches_total_compare) / row.brand_searches_total_compare >= 0.10;
        }

        if (!rule) return 0;

        const parents = treeRows.filter(r => r.isParent);

        return parents.filter(parent => {
            const children = treeRows.filter(r => r.parentId === parent.id && !r.isParent);
            if (children.length === 0) {
                // Pas d'enfants : on teste la règle sur le parent lui-même
                return rule!(parent);
            }
            // Au moins un enfant valide la règle
            return children.some(rule!);
        }).length;
    };

    const CustomFooter = ({
        paginationModel,
        onPageSizeChange
    }: any) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    backgroundColor: "#FAFAFA",
                    px: 2,
                    py: 1,
                    gap: 2,
                    borderBottomRightRadius: "8px",
                    borderBottomLeftRadius: "8px",

                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>Rows per page:</Typography>
                    <Select
                        size="small"
                        value={paginationModel.pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        sx={{ minWidth: 80 }}
                    >
                        {[5, 10, 20, 50].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </Select>

                    <GridPagination />
                </Stack>
            </Box>
        );
    };

    useEffect(() => {
        getCockpitData({
            current_range_date: selectedDateRange?.value,
            comparison_range_date: selectedComparisonPeriod?.value,
        });
    }, [selectedComparisonPeriod, selectedDateRange]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        workspace_name: false,
        blended_cpc_activated: false,
        blended_ctr_activated: false,
        ad_cost_activated: false,
        blended_conversions_total: false,
    });

    const handleChangeColumnVisibilityModel = React.useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
    }, []);

    const filterAndKeepParents = (rows: any, matchFn: any) => {
        // Indexe les rows par id
        const byId = Object.fromEntries(rows.map((r: any) => [r.id, r]));
        // Trouve les rows qui matchent le filtre
        const matchedRows = rows.filter(matchFn);

        // Pour chaque row matchée, ajoute son parent si besoin
        const rowsToShow = new Map();

        function addWithParents(row: any) {
            if (!row) return;
            if (rowsToShow.has(row.id)) return;
            // Marque le row comme matché si c'est lui qui match, sinon non
            rowsToShow.set(row.id, { ...row, matched: matchFn(row) });
            if (row.parentId) {
                addWithParents(byId[row.parentId]);
            }
        }

        matchedRows.forEach((row: any) => addWithParents(row));
        return Array.from(rowsToShow.values());
    }

    const filteredRows = React.useMemo(() => {
        // Sélecteur de filtre selon l'onglet
        let matchFn: any = () => true; // par défaut (All)
        if (selectedTab === 'Blended CTR dropped of 3pp') {
            matchFn = (row: any) =>
                typeof row.blended_ctr === 'number' &&
                typeof row.blended_ctr_compare === 'number' &&
                row.blended_ctr_compare &&
                (row.blended_ctr * 100 - row.blended_ctr_compare * 100) <= -3;
        }
        if (selectedTab === 'Blended CPC increased of 5%') {
            matchFn = (row: any) =>
                typeof row.blended_cpc_total === 'number' &&
                typeof row.blended_cpc_total_compare === 'number' &&
                row.blended_cpc_total_compare &&
                (row.blended_cpc_total - row.blended_cpc_total_compare) / row.blended_cpc_total_compare >= 0.05;
        }
        if (selectedTab === 'Brand Searches increased of 10%') {
            matchFn = (row: any) =>
                typeof row.brand_searches_total === 'number' &&
                typeof row.brand_searches_total_compare === 'number' &&
                row.brand_searches_total_compare &&
                (row.brand_searches_total - row.brand_searches_total_compare) / row.brand_searches_total_compare >= 0.10;
        }
        // Filtres + parents, sinon tout
        return selectedTab === 'All' ? treeRows : filterAndKeepParents(treeRows, matchFn);
    }, [treeRows, selectedTab]);

    useEffect(() => {
        dispatch(setFilterDate({
            start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString()
        }))

        dispatch(setFilterCompare(false))

        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }))
    }, [])

    return (
        <>
            <Box>
                <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"} sx={{ borderBottom: "1px solid rgba(145, 158, 171, 0.20)" }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: "24px",
                            gap: 2,
                        }}
                    >
                        {tabs.map((tab) => {
                            const isActive = selectedTab === tab.label;
                            return (
                                <Box
                                    key={tab.label}
                                    onClick={() => setSelectedTab(tab.label)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        fontWeight: isActive ? 600 : 500,
                                        px: .5,
                                        py: 1,
                                        borderRadius: 1,
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        color: isActive
                                            ? 'black'
                                            : '#919EAB',
                                        position: 'relative',
                                    }}
                                >
                                    {tab.label}
                                    <Box
                                        sx={{
                                            backgroundColor: tab?.countBadge,
                                            color: tab?.color,
                                            fontWeight: 700,
                                            px: 1,
                                            py: .5,
                                            fontSize: 12,
                                            borderRadius: 1,
                                            minWidth: 20,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {getTabCount(tab.label)}
                                    </Box>
                                    {isActive && (
                                        <Box
                                            sx={{
                                                content: '""',
                                                position: 'absolute',
                                                bottom: -8,
                                                left: 0,
                                                right: 0,
                                                height: 3,
                                                backgroundColor:
                                                    theme.palette.mode === 'dark' ? '#fff' : '#000',
                                                borderRadius: 2,
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>


                    <Box display="flex" justifyContent="flex-end" mb={2} px={"24px"}>
                        {["admin", "brand specialist"].includes(role) && !demoMode && (
                            <Stack display={'flex'} flexDirection={"row"} spacing={1}>
                                <IconButton
                                    onClick={() => setViewMode('list')}
                                    sx={{
                                        borderRadius: "8px",
                                        bgcolor: viewMode === 'list' ? 'white' : 'transparent',
                                        width: 35,
                                        height: 35,
                                        color: viewMode === 'list' ? 'rgba(51, 60, 71, 1)' : 'rgba(145, 158, 171, 1)',
                                    }}
                                >
                                    <Iconify icon="solar:list-outline" />
                                </IconButton>

                                <IconButton
                                    onClick={() => setViewMode('grid')}
                                    sx={{
                                        borderRadius: 1.5,
                                        bgcolor: viewMode === 'grid' ? 'white' : 'transparent',
                                        color: viewMode === 'grid' ? 'rgba(51, 60, 71, 1)' : 'rgba(145, 158, 171, 1)',
                                        width: 35,
                                        height: 35,
                                    }}
                                >
                                    <Iconify icon="akar-icons:dot-grid" />
                                </IconButton>
                            </Stack>
                        )}
                    </Box>
                </Box>

                <Box px={'24px'} sx={{ marginTop: "20px" }}>
                    {isLoading ? (
                        <Skeleton width={'100%'} height={700} variant="rectangular" animation="wave" sx={{
                            borderRadius: '8px',
                        }} />
                    ) : (
                        <DataGridPro
                            sx={{
                                height: "100%",
                                border: "1px solid rgba(145, 158, 171, 0.2)",
                                borderRadius: '8px',
                                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                                    outline: 'none !important',
                                },
                                '& .MuiDataGrid-columnHeader, .MuiDataGrid-filler': {
                                    backgroundColor: 'rgba(226, 229, 233, 0.16) !important',
                                    height: "41px!important",
                                    color: "rgba(99, 115, 129, 1)",
                                    fontWeight: 'normal'
                                },

                                width: '100%',
                                '& .MuiDataGrid-cell': {
                                    backgroundColor: 'white!important',
                                    overflow: 'visible !important',
                                    border: "1px solid rgba(145, 158, 171, 0.2)",
                                    color: 'rgba(51, 60, 71, 1)'
                                },
                                '& .MuiDataGrid-virtualScroller': {
                                    overflow: 'hidden !important', // supprime scroll vertical
                                },
                                '& .MuiDataGrid-main': {
                                    overflow: 'hidden !important', // supprime scroll horizontal
                                },
                                '& .MuiDataGrid-cellContent': {
                                    overflow: 'visible !important',
                                },
                                '& .highlighted-row': {
                                    opacity: 0.3,
                                },
                                overflow: 'visible !important',
                            }}
                            autoHeight={true}
                            apiRef={apiRef}
                            loading={isLoading}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
                            rows={filteredRows}
                            pinnedColumns={{ left: ['__tree_data_group__', 'Actions'] }}
                            columns={columns}
                            density="standard"
                            disableRowSelectionOnClick
                            treeData
                            getTreeDataPath={getTreeDataPath}
                            groupingColDef={{
                                headerName: 'Workspace',
                                width: 250,
                                sortable: true,
                                valueGetter: (params: any) => params?.row?.workspace_name || params?.row?.client,
                            }}
                            paginationModel={paginationModel}
                            pagination={true}
                            onPaginationModelChange={setPaginationModel}
                            getRowClassName={(params) => params.row.matched ? '' : selectedTab !== 'All' ? 'highlighted-row' : ''}
                            hideFooterSelectedRowCount
                            slots={{
                                toolbar: CustomToolbar,
                                footer: () => (
                                    <CustomFooter
                                        paginationModel={paginationModel}
                                        onPageSizeChange={(newSize: number) =>
                                            setPaginationModel((prev) => ({ ...prev, pageSize: newSize, page: 0 }))
                                        }
                                        rowCount={treeRows.length}
                                    />
                                )
                            }}
                        />
                    )}
                </Box>
            </Box>
        </>
    );
}